/**
 * Route guard for pages that require an active user WHO HAS a family.
 *
 * Expected to be wrapped inside `<RequireAuth>`, which handles the
 * authentication + active-status chain. This component only adds
 * the final "has a family" check. Active users without a family
 * are redirected to /family/create so they can set one up.
 *
 * Special case: if the user has a pending invitation token stashed
 * in sessionStorage (set by the `AcceptInvitationPage` just before
 * it sent them to sign in), redirect to `/invite/:token` instead
 * of `/family/create`. That way, an invitee who clicks the
 * invitation link and then signs in lands back on the accept page,
 * not the "create your own family" page.
 */

import type { ReactNode } from "react";
import { Navigate } from "react-router-dom";

import { LoadingShell } from "@/components/LoadingShell";
import { useAuth } from "@/features/auth/useAuth";
import {
  PENDING_INVITE_TOKEN_KEY,
  consumePendingInviteToken,
} from "@/features/invitations/pendingInvite";

interface Props {
  children: ReactNode;
}

export function RequireFamily({ children }: Props) {
  const { isLoading, family, isActive } = useAuth();

  if (isLoading) {
    return <LoadingShell />;
  }

  if (isActive && family === null) {
    const pending = consumePendingInviteToken();
    if (pending) {
      return <Navigate to={`/invite/${pending}`} replace />;
    }
    return <Navigate to="/family/create" replace />;
  }

  return <>{children}</>;
}

// Re-export for convenience so callers can `import { PENDING_INVITE_TOKEN_KEY }
// from "@/features/auth/RequireFamily"` too. The canonical home is still
// features/invitations/pendingInvite.ts.
export { PENDING_INVITE_TOKEN_KEY };
